import { StaticModal, createModal } from '@nexelem/modals';
import { Button } from '@nexelem/components';
import { Trans } from 'react-i18next';
import { useCustomTranslation } from '../../../../../../hooks';
import { useGraphneTaskRemoveFromPlan } from '../../../../../../queryHooks/productionPlanHooks';
import { TaskView } from '@nexelem/graphne-api';
import { useFormatTaskDuration } from '../../../../../../components/organisms/ProductionPlans/Gantt/hooks/useFormatTaskDuration';

type RemoveFromPlanModalProps = {
  task: TaskView;
};

export const RemoveFromPlanModal = ({ task }: RemoveFromPlanModalProps) => {
  const { tWrapper } = useCustomTranslation('products.productionPlan.removeFromPlanModal');
  const removeFromPlan = useGraphneTaskRemoveFromPlan(task.id.toString(), task.name);
  const formatTaskDuration = useFormatTaskDuration();

  const handleSubmit = () => {
    removeFromPlan.mutate(undefined, {
      onSuccess: closeRemoveTaskFromPlanModal,
    });
  };

  const taskDuration =
    task.start && task.end
      ? formatTaskDuration({ startDate: new Date(task.start), endDate: new Date(task.end) })
      : null;

  return (
    <StaticModal maxWidth="lg" onClose={closeRemoveTaskFromPlanModal}>
      <StaticModal.Header withCloseButton>{tWrapper('title')}</StaticModal.Header>
      <StaticModal.Content className="flex flex-col gap-6">
        <div className="flex flex-col gap-1 rounded-xs border border-border-container bg-surface-container-lighter px-4 py-3 text-s11-sb text-text-secondary-darker">
          <div className="flex gap-2 uppercase">
            <span>{tWrapper('lotId')}</span>
            <span>{task.name}</span>
          </div>
          <div className="flex justify-between text-text-primary">
            <span className="text-s16-sb">{task.productDetails.name}</span>
            <div className="flex items-baseline gap-2">
              <span className="text-s16-sb">{task.progress.totalAmount.amount}</span>
              <span className="text-s16">{task.progress.totalAmount.unit.name}</span>
            </div>
          </div>
          <div className="flex justify-between uppercase">
            <div className="flex gap-2" style={{ color: `#${task.queue?.area.hex}` }}>
              <span>{task.queue?.shortName || '-'}</span>
              <span>({task.queue?.area.name || '-'})</span>
            </div>
            <span className="text-text-secondary">{taskDuration}</span>
          </div>
        </div>
        <span className="text-s18">
          <Trans components={{ strong: <strong /> }}>{tWrapper('content')}</Trans>
        </span>
        <span className="text-s18">{tWrapper('content2')}</span>
      </StaticModal.Content>
      <StaticModal.Footer>
        <Button isLoading={removeFromPlan.isPending} variant="outlined" onClick={handleSubmit}>
          {tWrapper('confirmButton')}
        </Button>
        <Button variant="contained" onClick={closeRemoveTaskFromPlanModal}>
          {tWrapper('cancel')}
        </Button>
      </StaticModal.Footer>
    </StaticModal>
  );
};

export const { openRemoveTaskFromPlanModal, closeRemoveTaskFromPlanModal } = createModal(RemoveFromPlanModal, {
  name: 'RemoveTaskFromPlanModal',
});
